import { CHART_LABEL_SKIP_LIST, EMAIL_ANONYMIZED_SUFFIX, } from './demo-mode.constants';
import { DISTRIBUTOR_NAMES, CLIENT_NAMES, CAMPUS_NAMES, MARKET_NAMES, MACHINE_LOCATION_NAMES, } from './demo-mode-human-names';
/** Map for chart-style labels (string[] without entity IDs) - keyed by original string */
var chartLabelsMap = new Map();
/** Maps for analytics labels by breakdown dimension - keyed by (entityType, original) */
var labelMapsByEntity = {
    distributor: new Map(),
    market: new Map(),
    client: new Map(),
    campus: new Map(),
    machine: new Map(),
};
/** Map for anonymizing emails (approvers, createdBy, etc.) */
var emailMap = new Map();
function getOrCreateAnonymizedEmail(original) {
    if (!original || typeof original !== 'string' || !original.includes('@')) {
        return original;
    }
    var anonymized = emailMap.get(original);
    if (!anonymized) {
        var index = emailMap.size + 1;
        anonymized = "user" + index + EMAIL_ANONYMIZED_SUFFIX;
        emailMap.set(original, anonymized);
    }
    return anonymized;
}
/** Heuristic: "City, ST" format suggests market/location */
function looksLikeLocation(str) {
    return /^.+, [A-Za-z]{2}$/.test(str.trim());
}
/** Heuristic: label looks like a date (time series) - don't anonymize */
function looksLikeDate(str) {
    var s = str.trim();
    if (!s)
        return false;
    // ISO date, "Jan 2024", "1/15/2024", "2024-01", "January 2024", etc.
    if (/^\d{4}-\d{2}(-\d{2})?$/.test(s))
        return true; // 2024-01 or 2024-01-15
    if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(s))
        return true; // 1/15/2024
    if (/^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,4}$/i.test(s))
        return true; // Jan 2024, Jan 15 2024
    if (/^\d{1,2}\/\d{1,2}$/.test(s))
        return true; // 1/15
    return false;
}
function getHumanName(entityType, index) {
    var names = entityType === 'distributor' ? DISTRIBUTOR_NAMES
        : entityType === 'client' ? CLIENT_NAMES
            : entityType === 'campus' ? CAMPUS_NAMES
                : entityType === 'market' ? MARKET_NAMES
                    : entityType === 'machine' ? MACHINE_LOCATION_NAMES
                        : [];
    var base = names[(index - 1) % names.length];
    return index > names.length ? base + " " + (Math.floor((index - 1) / names.length) + 1) : base;
}
function getOrCreateLabelForEntity(original, entityType) {
    var map = labelMapsByEntity[entityType];
    var key = original.trim();
    if (!key || CHART_LABEL_SKIP_LIST.some(function (skip) { return key === skip; }))
        return original;
    var anonymized = map.get(key);
    if (!anonymized) {
        var index = map.size + 1;
        anonymized = getHumanName(entityType, index);
        map.set(key, anonymized);
    }
    return anonymized;
}
function getOrCreateChartLabel(original) {
    var trimmed = original.trim();
    if (CHART_LABEL_SKIP_LIST.some(function (skip) { return trimmed === skip || !trimmed; })) {
        return original;
    }
    var anonymized = chartLabelsMap.get(original);
    if (!anonymized) {
        var index = chartLabelsMap.size + 1;
        anonymized = looksLikeLocation(original)
            ? getHumanName('market', index)
            : "Label " + index;
        chartLabelsMap.set(original, anonymized);
    }
    return anonymized;
}
var ENTITY_CONFIG = {
    distributor: {
        idFields: ['disti_id', 'distiid', 'distiId'],
        nameFields: ['name', 'disti_name', 'distiName'],
    },
    market: {
        idFields: ['market_id', 'marketid', 'marketId'],
        nameFields: ['market_name', 'marketName'],
    },
    client: {
        idFields: ['client_id', 'clientid', 'clientId'],
        nameFields: [
            'company_name',
            'companyName',
            'client_name',
            'clientName',
            'prefix_text',
            'prefix',
            'access_id',
            'accessId',
        ],
    },
    campus: {
        idFields: ['campus_id', 'campusid', 'campusId'],
        nameFields: ['name', 'campus_name', 'campusName'],
    },
    machine: {
        idFields: ['machine_id', 'machineid', 'machineId', 'id'],
        nameFields: ['machine_name', 'machineName', 'serial_no', 'serialNo', 'name'],
    },
};
/** Top-machines API uses plain 'id'; only treat as machine when value looks like a machine serial (avoids drink types) */
function looksLikeMachineId(id) {
    var s = String(id);
    return /^[A-Z0-9][A-Za-z0-9_-]*$/.test(s) && s.length >= 4;
}
/** Object has inventory-specific fields: name is product label (BEANS, COCOA, etc.), not machine name */
function looksLikeInventoryItem(obj) {
    return (obj.canSize !== undefined ||
        obj.remSize !== undefined ||
        obj.slotNo !== undefined ||
        obj.lowLevel !== undefined ||
        obj.emptyLevel !== undefined ||
        obj.colorBox !== undefined);
}
var idMaps = {
    distributor: new Map(),
    market: new Map(),
    client: new Map(),
    campus: new Map(),
    machine: new Map(),
};
function getOrCreateGenericName(entityType, id) {
    var map = idMaps[entityType];
    var key = String(id);
    var name = map.get(key);
    if (!name) {
        var index = map.size + 1;
        name = getHumanName(entityType, index);
        map.set(key, name);
    }
    return name;
}
var clientPrefixMap = new Map();
function getOrCreateClientPrefix(id) {
    var key = String(id);
    var prefix = clientPrefixMap.get(key);
    if (!prefix) {
        var index = clientPrefixMap.size + 1;
        prefix = "CLT" + index;
        clientPrefixMap.set(key, prefix);
    }
    return prefix;
}
function detectEntity(obj) {
    for (var _i = 0, _a = Object.entries(ENTITY_CONFIG); _i < _a.length; _i++) {
        var _b = _a[_i], entityType = _b[0], config = _b[1];
        for (var _c = 0, _d = config.idFields; _c < _d.length; _c++) {
            var idField = _d[_c];
            var id = obj[idField];
            if (id != null && (typeof id === 'string' || typeof id === 'number')) {
                if (idField === 'id' && entityType === 'machine' && !looksLikeMachineId(id))
                    continue;
                return { type: entityType, id: id };
            }
        }
    }
    return null;
}
/** Detect all entities in an object (used when object has multiple entity types in one row) */
function detectAllEntities(obj) {
    var entities = [];
    for (var _i = 0, _a = Object.entries(ENTITY_CONFIG); _i < _a.length; _i++) {
        var _b = _a[_i], entityType = _b[0], config = _b[1];
        for (var _c = 0, _d = config.idFields; _c < _d.length; _c++) {
            var idField = _d[_c];
            var id = obj[idField];
            if (id != null && (typeof id === 'string' || typeof id === 'number')) {
                if (idField === 'id' && entityType === 'machine' && !looksLikeMachineId(id))
                    continue;
                entities.push({ type: entityType, id: id });
                break;
            }
        }
    }
    return entities;
}
/** Anonymize a single label string based on context (breakdownBy). Used for labels array and contextualData keys. */
function anonymizeLabel(label, context) {
    var breakdownBy = context && context.breakdownBy;
    if (breakdownBy === 'drink' || breakdownBy === 'product')
        return label;
    if (looksLikeDate(label))
        return label;
    var entityType = breakdownBy === 'distributor' ? 'distributor'
        : breakdownBy === 'market' ? 'market'
            : breakdownBy === 'client' ? 'client'
                : breakdownBy === 'campus' ? 'campus'
                    : breakdownBy === 'machine' ? 'machine'
                        : null;
    if (entityType)
        return getOrCreateLabelForEntity(label, entityType);
    return getOrCreateChartLabel(label);
}
function anonymizeValue(value, context) {
    if (value === null || value === undefined) {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(function (item) { return anonymizeValue(item, context); });
    }
    if (typeof value === 'object' && value !== null) {
        var obj = value;
        var entity = detectEntity(obj);
        var allEntities = detectAllEntities(obj);
        var result = {};
        var _loop_1 = function (k, v) {
            var lowerKey = k.toLowerCase();
            // Chart-style labels array: anonymize based on breakdownBy (analytics) or generic
            if (lowerKey === 'labels' && Array.isArray(v) && v.every(function (l) { return typeof l === 'string'; })) {
                result[k] = v.map(function (l) { return anonymizeLabel(l, context); });
                return "continue";
            }
            // contextualData: keys are label strings, rekey with same anonymization as labels
            if (lowerKey === 'contextualdata' && v && typeof v === 'object' && !Array.isArray(v)) {
                var newContextual = {};
                for (var _i = 0, _a = Object.entries(v); _i < _a.length; _i++) {
                    var _b = _a[_i], key = _b[0], val = _b[1];
                    var newKey = anonymizeLabel(key, context);
                    newContextual[newKey] = anonymizeValue(val, context);
                }
                result[k] = newContextual;
                return "continue";
            }
            // Check if this key is a name field for any entity in the object (handles multi-entity rows like campus inventory)
            var matchingEntity = null;
            for (var _c = 0, allEntities_1 = allEntities; _c < allEntities_1.length; _c++) {
                var e = allEntities_1[_c];
                if (ENTITY_CONFIG[e.type].nameFields.some(function (nf) { return nf.toLowerCase() === lowerKey; })) {
                    matchingEntity = e;
                    break;
                }
            }
            if (!matchingEntity && entity && ENTITY_CONFIG[entity.type].nameFields.some(function (nf) { return nf.toLowerCase() === lowerKey; })) {
                matchingEntity = entity;
            }
            if (matchingEntity) {
                // Inventory items: name is product label (BEANS, COCOA, etc.), not machine/entity name - do not anonymize
                if (lowerKey === 'name' && looksLikeInventoryItem(obj)) {
                    result[k] = v;
                }
                else if (lowerKey === 'prefix_text' || lowerKey === 'prefix') {
                    result[k] = getOrCreateClientPrefix(matchingEntity.id);
                }
                else if (lowerKey === 'access_id' || lowerKey === 'accessid') {
                    result[k] = getOrCreateClientPrefix(matchingEntity.id) + 'accessid';
                }
                else {
                    result[k] = getOrCreateGenericName(matchingEntity.type, matchingEntity.id);
                }
            }
            else if (lowerKey === 'account' && typeof v === 'string' && v.trim()) {
                result[k] = getOrCreateChartLabel(v);
            }
            else if (lowerKey === 'machine' && typeof v === 'string' && looksLikeMachineId(v)) {
                // Analytics stockout rows/heatmap: machine field holds machine ID string
                result[k] = getOrCreateGenericName('machine', v);
            }
            else if ((lowerKey === 'createdby' || lowerKey === 'updatedby') && typeof v === 'string') {
                result[k] = getOrCreateAnonymizedEmail(v);
            }
            else if (lowerKey === 'approvers' && Array.isArray(v)) {
                result[k] = v.map(function (e) {
                    return typeof e === 'string' ? getOrCreateAnonymizedEmail(e) : anonymizeValue(e, context);
                });
            }
            else {
                result[k] = anonymizeValue(v, context);
            }
        };
        for (var _i = 0, _a = Object.entries(obj); _i < _a.length; _i++) {
            var _b = _a[_i], k = _b[0], v = _b[1];
            _loop_1(k, v);
        }
        // Machine: add display IDs for UI. For camelCase/plain id (dashboard widgets): replace in place so components need no changes.
        // For snake_case machine_id (machine list): keep for routing, add machine_id_display for display.
        var machineEntity = allEntities.find(function (e) { return e.type === 'machine'; });
        if (machineEntity) {
            var displayId = getOrCreateGenericName('machine', machineEntity.id);
            // machine_id: keep for routing (links), add _display for UI
            result.machine_id_display = displayId;
            // machineId, id: replace in place so Fleet Health, Top Machines, Recent Activities display anonymized without component changes
            if (obj.machineId !== undefined)
                result.machineId = displayId;
            if (obj.id !== undefined)
                result.id = displayId;
        }
        return result;
    }
    return value;
}
/**
 * Recursively anonymizes name fields in API response data.
 * Uses deterministic mapping so the same entity ID always maps to the same generic name.
 * @param context.breakdownBy - For analytics: 'drink'|'product' (no anonymization), 'distributor'|'market'|'client'|'campus'|'machine' (entity-specific), or dates (no anonymization)
 */
export function anonymize(data, context) {
    if (data === null || data === undefined) {
        return data;
    }
    return anonymizeValue(data, context);
}
